import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { Country, State, City } from "../../util/data";
import { createLocation, updateLocation } from "../../redux/locations/locationSlice";
import CustomInput from "../Common/CustomInput";
import CustomButton from "../Common/CustomButton";
import PhoneNumberInput from "../Common/PhoneNumberInput";
import CustomSelect from "../Common/CustomSelect";
import { Building2 } from "lucide-react";

const initialFormState = {
  displayName: "",
  name: "",
  phoneNumber: "",
  dialCode: "+91",
  address1: "",
  address2: "",
  country: "",
  state: "",
  city: "",
  zipCode: "",
  HfrId1: "",
  HfrId2: "",
};

const LocationInfo = ({
  locationFormData,
  UserBasicInfo,
  setLocationFormData,
  toggleCreateProviderForm,
  id,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { createStatus: isLoading } = useSelector(state => state.locations);
  const [invalidFields, setInvalidFields] = useState({});
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  
  const tenantId = useMemo(() => Cookies.get("TenantId"), []);

  const { statesByCountry, citiesByState } = useMemo(() => ({
    statesByCountry: State.reduce((acc, state) => {
      if (!acc[state.country_id]) acc[state.country_id] = [];
      acc[state.country_id].push(state);
      return acc;
    }, {}),
    citiesByState: City.reduce((acc, city) => {
      if (!acc[city.state_id]) acc[city.state_id] = [];
      acc[city.state_id].push(city);
      return acc;
    }, {}),
  }), []);

  useEffect(() => {
    return () => setLocationFormData(initialFormState);
  }, [setLocationFormData]);

  useEffect(() => {
    if (locationFormData.country) {
      const countryObject = Country.find(c => c.name === locationFormData.country);
      if (countryObject) {
        setFilteredStates(statesByCountry[countryObject.id] || []);
      }
    }
  }, [locationFormData.country, statesByCountry]);

  useEffect(() => {
    if (locationFormData.state) {
      const stateObject = filteredStates.find(s => s.name === locationFormData.state);
      if (stateObject) {
        setFilteredCities(citiesByState[stateObject.id] || []);
      }
    }
  }, [locationFormData.state, filteredStates, citiesByState]);

  const handleChange = useCallback((name, value, selectedOption) => {
    if (name === "zipCode") {
      value = value.replace(/[^0-9]/g, "").slice(0, 6);
    }

    setLocationFormData(prev => ({ ...prev, [name]: value }));
    setInvalidFields(prev => ({ ...prev, [name]: "" }));

    if (!selectedOption) return;

    switch (name) {
      case "country":
        setFilteredStates(statesByCountry[selectedOption.id] || []);
        setFilteredCities([]);
        setLocationFormData(prev => ({ ...prev, state: "", city: "" }));
        break;
      case "state":
        setFilteredCities(citiesByState[selectedOption.id] || []);
        setLocationFormData(prev => ({ ...prev, city: "" }));
        break;
      default:
        break;
    }
  }, [statesByCountry, citiesByState, setLocationFormData]);

  const validateForm = useCallback(() => {
    const newInvalidFields = {};
    const phoneRegex = /^[0-9]{10}$/;
    const pincodeRegex = /^[0-9]{6}$/;
    
    const requiredFields = [
      "displayName", "name", "phoneNumber", "dialCode", 
      "address1", "country", "state", "city", "zipCode"
    ];
    
    requiredFields.forEach(key => {
      if (!locationFormData[key]) {
        newInvalidFields[key] = `Please enter ${key}`;
      }
    });

    if (locationFormData.phoneNumber && !phoneRegex.test(locationFormData.phoneNumber)) {
      newInvalidFields.phoneNumber = "Please enter a valid phone number";
    }
    
    if (locationFormData.zipCode && !pincodeRegex.test(locationFormData.zipCode)) {
      newInvalidFields.zipCode = "Pincode must be exactly 6 digits.";
    }
    
    if (locationFormData.HfrId1 && locationFormData.HfrId1 !== locationFormData.HfrId2) {
      newInvalidFields.HfrId2 = "HfrId2 must be the same as HfrId1";
    }

    setInvalidFields(newInvalidFields);
    return Object.keys(newInvalidFields).length === 0;
  }, [locationFormData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const { displayName, name, dialCode, phoneNumber, address1, address2, city, state, zipCode, country, HfrId1 } = locationFormData;

    const locationData = {
      displayName,
      name,
      HfrId: HfrId1,
      address: {
        addressLine1: address1,
        addressLine2: address2,
        city,
        state,
        zipCode,
        country
      },
      phoneNumber: {
        dialCode,
        value: phoneNumber
      },
    };

    try {
      const action = id
        ? updateLocation({ id, newLocation: locationData })
        : createLocation({ ...locationData, tenantId });
        
      await dispatch(action).unwrap();
      toast.success(`Location ${id ? "Updated" : "Created"} successfully!`);

      setLocationFormData(initialFormState);
      toggleCreateProviderForm();
      navigate("/providers/locations");
    } catch (error) {
      toast.error(error);
    }
  }, [validateForm, id, locationFormData, tenantId, dispatch, navigate, setLocationFormData, toggleCreateProviderForm]);

  const handleDialCodeChange = useCallback((value) => {
    setLocationFormData(prev => ({ ...prev, dialCode: value }));
  }, [setLocationFormData]);

  const renderField = useCallback((elem) => {
    const commonProps = {
      id: elem.id,
      label: elem.label,
      placeholder: elem.placeholder,
      value: locationFormData[elem.id] || "",
      isInvalid: invalidFields[elem.id],
      required: elem.required
    };

    switch (elem.id) {
      case "phoneNumber":
        return (
          <div className="relative" key={elem.id}>
            <PhoneNumberInput
              {...commonProps}
              dialCode={locationFormData.dialCode}
              onChangeDialCode={handleDialCodeChange}
              onChangeNumber={(value) => handleChange(elem.id, value)}
            />
          </div>
        );
      case "country":
        return (
          <div className="relative" key={elem.id}>
            <CustomSelect
              {...commonProps}
              options={Country}
              onChange={handleChange}
            />
          </div>
        );
      case "state":
        return (
          <div className="relative" key={elem.id}>
            <CustomSelect
              {...commonProps}
              options={filteredStates}
              onChange={handleChange}
            />
          </div>
        );
      case "city":
        return (
          <div className="relative" key={elem.id}>
            <CustomSelect
              {...commonProps}
              options={filteredCities}
              onChange={handleChange}
            />
          </div>
        );
      default:
        return (
          <div className="relative" key={elem.id}>
            <CustomInput
              {...commonProps}
              type={elem.type}
              onchange={(e) => handleChange(elem.id, e.target.value)}
            />
          </div>
        );
    }
  }, [locationFormData, invalidFields, handleChange, handleDialCodeChange, filteredStates, filteredCities]);
 
  return (
    <div className="h-max w-full bg-white py-8">
      <div className="w-full h-max mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 bg-brand-500">
            <div className="flex items-center space-x-3">
              <Building2 className="h-6 w-6 text-white" />
              <h2 className="text-xl font-bold text-white">
                {id ? "Update Location" : "Add New Location"}
              </h2>
            </div>
          </div>
          
          <form className="p-6" onSubmit={handleSubmit}>
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {UserBasicInfo.map(renderField)}
              </div>
              
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={toggleCreateProviderForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <CustomButton 
                  type="submit"
                  text="Submit"
                  disabled={isLoading}
                  loading={isLoading}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default React.memo(LocationInfo);